// A tiny interruptible spring in the designer-friendly form Apple uses:
// `response` is roughly the time to reach the target, `damping` 1 settles
// without overshoot. Every animation starts from the live value and velocity,
// so a gesture can grab it mid-flight and hand its own velocity back.
export type SpringOptions = { response?: number; damping?: number; velocity?: number; onRest?: () => void }

export class Spring {
  value: number
  velocity = 0
  target: number
  private frame = 0
  private last = 0
  private stiffness = 0
  private friction = 0
  private onRest?: () => void

  constructor(value: number, private readonly onChange: () => void, private readonly precision = 0.1) {
    this.value = value
    this.target = value
  }

  get isAnimating() { return this.frame !== 0 }

  set(value: number) {
    this.stop()
    this.value = value
    this.target = value
    this.velocity = 0
    this.onChange()
  }

  to(target: number, { response = 0.35, damping = 1, velocity, onRest }: SpringOptions = {}) {
    this.target = target
    if (velocity !== undefined) this.velocity = velocity
    this.stiffness = (2 * Math.PI / response) ** 2
    this.friction = 4 * Math.PI * damping / response
    this.onRest = onRest
    if (!this.frame) {
      this.last = window.performance.now()
      this.frame = window.requestAnimationFrame(this.step)
    }
  }

  stop() {
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.frame = 0
    this.onRest = undefined
  }

  private step = (now: number) => {
    // Integrate in small fixed steps so stiff springs stay stable on slow frames.
    let remaining = Math.min((now - this.last) / 1000, 1 / 30)
    this.last = now
    while (remaining > 0) {
      const h = Math.min(remaining, 1 / 240)
      const acceleration = -this.stiffness * (this.value - this.target) - this.friction * this.velocity
      this.velocity += acceleration * h
      this.value += this.velocity * h
      remaining -= h
    }
    const resting = Math.abs(this.value - this.target) < this.precision && Math.abs(this.velocity) < this.precision * 10
    if (resting) {
      this.value = this.target
      this.velocity = 0
      this.frame = 0
    } else {
      this.frame = window.requestAnimationFrame(this.step)
    }
    this.onChange()
    if (resting) {
      const onRest = this.onRest
      this.onRest = undefined
      onRest?.()
    }
  }
}

// Where a flick would come to rest with scroll-like deceleration.
export function project(velocity: number, decelerationRate = 0.998) {
  return (velocity / 1000) * decelerationRate / (1 - decelerationRate)
}

// Progressive resistance past a boundary instead of a hard stop.
export function rubberband(overshoot: number, dimension: number, constant = 0.55) {
  if (!dimension) return 0
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}
